import type { PerfilTanque } from '../caminhaoTanque/perfil'
import type { IdFonte } from '../fontes'
import type { EstadoTransporte } from '../produtos'
import { situacaoDasAberturas, type PoseTanque } from './pose'

/**
 * Sequência do transbordo, avaliada passo a passo.
 *
 * É uma sequência DIDÁTICA deste laboratório, montada sobre boas práticas
 * (aterramento e equipotencialização antes de qualquer conexão — NFPA 77,
 * API RP 2003). Não substitui o procedimento operacional da corporação nem a
 * orientação do fabricante e do expedidor do produto.
 *
 * A avaliação é uma função pura da sequência de ações do aluno: cada ação
 * é conferida contra o que já foi feito antes dela, e o erro diz o que faltou.
 */

export type IdAcao =
  | 'estabelecerZonas'
  | 'eliminarIgnicao'
  | 'monitorarAtmosfera'
  | 'posicionarReceptor'
  | 'aterrarAvariado'
  | 'aterrarReceptor'
  | 'equipotencializar'
  | 'conectarRetornoVapor'
  | 'conectarMangoteLiquido'
  | 'verificarConexoes'
  | 'iniciarTransferencia'
  | 'controlarVazao'

export interface DefinicaoAcao {
  id: IdAcao
  nome: string
  porque: string
  /** Ações que precisam ter sido feitas antes. */
  requer: readonly IdAcao[]
  /** Só para gás liquefeito (retorno de vapor). */
  somenteGasLiquefeito?: boolean
  /** A ação precisa de uma abertura do tanque avariado. */
  usaAbertura?: 'liquido' | 'vapor'
  fonte?: IdFonte
}

export const ACOES: readonly DefinicaoAcao[] = [
  {
    id: 'estabelecerZonas',
    nome: 'Estabelecer zonas e isolamento',
    porque: 'Ninguém entra na área sem a ZQ/ZM/ZF definida e o público afastado pelo GRE.',
    requer: [],
  },
  {
    id: 'eliminarIgnicao',
    nome: 'Eliminar fontes de ignição',
    porque: 'Motores, rádios não intrinsecamente seguros, celulares, chama: a atmosfera pode ficar inflamável a qualquer momento.',
    requer: ['estabelecerZonas'],
  },
  {
    id: 'monitorarAtmosfera',
    nome: 'Monitorar a atmosfera (explosímetro/LII e tóxicos)',
    porque: 'Monitoramento contínuo decide se é seguro aproximar o receptor e mantém a operação sob controle.',
    requer: ['estabelecerZonas'],
  },
  {
    id: 'posicionarReceptor',
    nome: 'Posicionar o caminhão receptor',
    porque: 'O receptor entra com o motor ligado: só depois do monitoramento, contra o vento ou de lado, nunca a favor do vento.',
    requer: ['eliminarIgnicao', 'monitorarAtmosfera'],
  },
  {
    id: 'aterrarAvariado',
    nome: 'Aterrar o veículo avariado (haste no solo)',
    porque: 'Escoa para a terra a carga eletrostática acumulada no tanque.',
    requer: ['estabelecerZonas'],
    fonte: 'nfpa77',
  },
  {
    id: 'aterrarReceptor',
    nome: 'Aterrar o caminhão receptor',
    porque: 'O receptor também acumula carga; sem aterramento, a primeira conexão pode gerar faísca.',
    requer: ['posicionarReceptor'],
    fonte: 'nfpa77',
  },
  {
    id: 'equipotencializar',
    nome: 'Equipotencializar os dois veículos',
    porque: 'Cabo entre os dois tanques: mesmo potencial, sem diferença que salte em faísca ao encostar o mangote.',
    requer: ['aterrarAvariado', 'aterrarReceptor'],
    fonte: 'apiRp2003',
  },
  {
    id: 'conectarRetornoVapor',
    nome: 'Conectar a linha de retorno de vapor',
    porque: 'Equilibra as pressões: o vapor deslocado no receptor volta ao avariado em vez de ir para a atmosfera.',
    requer: ['equipotencializar'],
    somenteGasLiquefeito: true,
    usaAbertura: 'vapor',
  },
  {
    id: 'conectarMangoteLiquido',
    nome: 'Conectar o mangote de líquido',
    porque: 'A transferência sai por uma abertura que, nesta pose, esteja no líquido e acessível.',
    requer: ['equipotencializar'],
    usaAbertura: 'liquido',
  },
  {
    id: 'verificarConexoes',
    nome: 'Verificar conexões e estanqueidade',
    porque: 'Vazamento na conexão no início da transferência é o pior momento para descobrir.',
    requer: ['conectarMangoteLiquido'],
  },
  {
    id: 'iniciarTransferencia',
    nome: 'Iniciar a transferência (bomba ou diferencial de pressão)',
    porque: 'Gás liquefeito vai por bomba/compressor com o retorno de vapor; líquido, por bomba ou gravidade.',
    requer: ['verificarConexoes', 'monitorarAtmosfera'],
  },
  {
    id: 'controlarVazao',
    nome: 'Controlar vazão e nível do receptor',
    porque: 'Vazão alta gera mais eletricidade estática; o receptor não pode ser cheio além do limite de enchimento.',
    requer: ['iniciarTransferencia'],
    fonte: 'nfpa77',
  },
]

export const acaoPorId = (id: IdAcao): DefinicaoAcao => ACOES.find((a) => a.id === id)!

/**
 * Pré-requisitos ainda não feitos, incluindo os indiretos, na ordem em que
 * aparecem em `ACOES`: dizer só "falta equipotencializar" esconderia que
 * nenhum dos dois veículos foi aterrado.
 */
function faltandoTransitivo(acao: DefinicaoAcao, feitas: ReadonlySet<IdAcao>, gasLiquefeito: boolean): IdAcao[] {
  const faltam = new Set<IdAcao>()
  const visitar = (id: IdAcao) => {
    for (const requisito of acaoPorId(id).requer) {
      if (feitas.has(requisito) || faltam.has(requisito)) continue
      if (acaoPorId(requisito).somenteGasLiquefeito && !gasLiquefeito) continue
      faltam.add(requisito)
      visitar(requisito)
    }
  }
  visitar(acao.id)
  return ACOES.map((a) => a.id).filter((id) => faltam.has(id))
}

export interface PassoDoAluno {
  acao: IdAcao
  /** Abertura do tanque avariado usada, para as conexões. */
  idAbertura?: string
}

export interface ContextoTransbordo {
  estado: EstadoTransporte
  tanque: PerfilTanque
  pose: PoseTanque
  enchimento: number
  /** O receptor ficou a favor do vento do avariado. */
  receptorAFavorDoVento: boolean
}

export interface AvaliacaoPasso {
  passo: PassoDoAluno
  correto: boolean
  mensagem: string
}

export interface AvaliacaoProcedimento {
  passos: AvaliacaoPasso[]
  /** Ações obrigatórias ainda não feitas corretamente. */
  pendentes: IdAcao[]
  concluido: boolean
  erros: number
}

const obrigatoria = (acao: DefinicaoAcao, estado: EstadoTransporte) =>
  !acao.somenteGasLiquefeito || estado === 'gasLiquefeitoPressurizado'

export function avaliarProcedimento(passos: readonly PassoDoAluno[], contexto: ContextoTransbordo): AvaliacaoProcedimento {
  const feitas = new Set<IdAcao>()
  const situacoes = situacaoDasAberturas(contexto.tanque, contexto.pose, contexto.enchimento)
  const gasLiquefeito = contexto.estado === 'gasLiquefeitoPressurizado'

  const avaliados = passos.map((passo): AvaliacaoPasso => {
    const acao = acaoPorId(passo.acao)
    const erro = (mensagem: string): AvaliacaoPasso => ({ passo, correto: false, mensagem })

    if (feitas.has(acao.id)) return { passo, correto: true, mensagem: 'Repetida — já estava feita.' }
    if (acao.somenteGasLiquefeito && !gasLiquefeito) {
      return erro('Retorno de vapor é para gás liquefeito sob pressão; este tanque transporta líquido.')
    }

    const faltando = faltandoTransitivo(acao, feitas, gasLiquefeito)
    if (faltando.length > 0) {
      const conexao = acao.id === 'conectarMangoteLiquido' || acao.id === 'conectarRetornoVapor'
      const nomes = faltando.map((f) => acaoPorId(f).nome.toLowerCase()).join('; ')
      return erro(
        conexao && faltando.includes('equipotencializar')
          ? `Conexão antes do aterramento e da equipotencialização: risco de faísca por eletricidade estática. Falta: ${nomes}.`
          : `Fora de ordem. Antes, falta: ${nomes}.`,
      )
    }

    if (acao.id === 'posicionarReceptor' && contexto.receptorAFavorDoVento) {
      return erro('O receptor ficou a favor do vento do tanque avariado: vapores chegam ao motor ligado. Reposicione contra o vento ou de lado.')
    }

    if (acao.usaAbertura) {
      const situacao = situacoes.find((s) => s.abertura.id === passo.idAbertura)
      if (!situacao) return erro('Escolha em qual abertura do tanque avariado conectar.')
      if (situacao.obstruida) return erro(`${situacao.abertura.nome} está prensada contra o solo nesta pose — inacessível.`)
      if (situacao.abertura.tipo === 'valvulaAlivio' || situacao.abertura.tipo === 'bocaVisita') {
        return erro(`${situacao.abertura.nome} não é ponto de conexão de transferência.`)
      }
      if (situacao.fase !== acao.usaAbertura) {
        return erro(
          acao.usaAbertura === 'liquido'
            ? `${situacao.abertura.nome} está na fase VAPOR nesta pose: por ela sairia gás, não líquido.`
            : `${situacao.abertura.nome} está na fase LÍQUIDA nesta pose: o retorno de vapor precisa de uma abertura no vapor.`,
        )
      }
    }

    if (acao.id === 'iniciarTransferencia' && gasLiquefeito && !feitas.has('conectarRetornoVapor')) {
      return erro('Gás liquefeito sem retorno de vapor: a pressão sobe no receptor e cai no avariado. Conecte o retorno antes.')
    }

    feitas.add(acao.id)
    return { passo, correto: true, mensagem: acao.porque }
  })

  const pendentes = ACOES.filter((a) => obrigatoria(a, contexto.estado) && !feitas.has(a.id)).map((a) => a.id)
  return {
    passos: avaliados,
    pendentes,
    concluido: pendentes.length === 0,
    erros: avaliados.filter((a) => !a.correto).length,
  }
}
